import { zodResolver } from "@hookform/resolvers/zod"
import { isPossiblePhoneNumber } from "react-phone-number-input"
import React, { useState } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { gql } from "~/__generated__"
import { useFormErrors } from "~/common/use-form-errors"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { Button } from "~/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "~/ui/dialog"
import { GraphqlError } from "~/ui/errors"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/ui/form"
import { PhoneInput } from "~/ui/phone-input"
import { PhoneVerificationStep } from "./components/PhoneVerificationStep"

const phoneFormSchema = z.object({
  phone: z
    .string()
    .min(1, { message: "Phone number is required" })
    .refine(isPossiblePhoneNumber, { message: "Invalid phone number" }),
})

type PhoneFormValues = z.infer<typeof phoneFormSchema>

const USER_PHONE_CREATE_MUTATION = gql(/* GraphQL */ `
  mutation AddPhoneDialogUserPhoneCreate($input: UserPhoneCreateInput!) {
    userPhoneCreate(input: $input) {
      userPhone {
        id
        phone
        phoneFormatted
        status
      }
    }
  }
`)

const USER_PHONE_VERIFY_MUTATION = gql(/* GraphQL */ `
  mutation AddPhoneDialogUserPhoneVerify($input: UserPhoneVerifyInput!) {
    userPhoneVerify(input: $input) {
      userPhone {
        id
        phone
        phoneFormatted
        status
      }
    }
  }
`)

interface AddPhoneDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export const AddPhoneDialog: React.FC<AddPhoneDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const [phoneSubmitted, setPhoneSubmitted] = useState(false)
  const [userPhoneId, setUserPhoneId] = useState<string>("")
  const [submittedPhone, setSubmittedPhone] = useState<string>("")
  const [userPhoneCreate, userPhoneCreateResult] = useSafeMutation(USER_PHONE_CREATE_MUTATION)
  const [userPhoneVerify, userPhoneVerifyResult] = useSafeMutation(USER_PHONE_VERIFY_MUTATION)

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneFormSchema),
    defaultValues: {
      phone: "",
    },
  })

  useFormErrors(phoneForm.setError, userPhoneCreateResult)

  const handleClose = () => {
    setPhoneSubmitted(false)
    setUserPhoneId("")
    setSubmittedPhone("")
    phoneForm.reset()
    onOpenChange(false)
  }

  const onPhoneSubmit = async (values: PhoneFormValues) => {
    const result = await userPhoneCreate({
      variables: {
        input: {
          input: {
            phone: values.phone,
          },
        },
      },
    })

    if (result.errors) {
      return
    }

    setUserPhoneId(result.data?.userPhoneCreate.userPhone.id || "")
    setSubmittedPhone(result.data?.userPhoneCreate.userPhone.phoneFormatted || values.phone)
    setPhoneSubmitted(true)
  }

  const handleVerify = async (code: string) => {
    const result = await userPhoneVerify({
      variables: {
        input: {
          userPhoneId,
          code,
        },
      },
    })

    if (result.errors) {
      return
    }

    onSuccess?.()
    handleClose()
  }

  const handleSendAgain = () => {
    setPhoneSubmitted(false)
    setSubmittedPhone("")
    phoneForm.reset()
  }

  if (phoneSubmitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enter Verification Code</DialogTitle>
          </DialogHeader>
          <PhoneVerificationStep
            phone={submittedPhone}
            onVerify={handleVerify}
            onSendAgain={handleSendAgain}
            onCancel={handleClose}
            verificationError={userPhoneVerifyResult.error}
            isVerifying={userPhoneVerifyResult.loading}
            description="A verification code was sent to the phone number provided. Please check your messages and input the code provided."
          />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Phone Number</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            A verification code will be sent to the phone number provided. This helps us verify your
            account.
          </p>
          <Form {...phoneForm}>
            <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-4">
              <FormField
                control={phoneForm.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-start">
                    <FormLabel className="text-left">Phone Number</FormLabel>
                    <FormControl className="w-full">
                      <PhoneInput defaultCountry="US" {...field} />
                    </FormControl>
                    <FormDescription className="text-left">Enter a phone number</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {userPhoneCreateResult.error && (
                <GraphqlError error={userPhoneCreateResult.error} variant="red-box" />
              )}
            </form>
          </Form>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={phoneForm.handleSubmit(onPhoneSubmit)}
            disabled={userPhoneCreateResult.loading}
          >
            {userPhoneCreateResult.loading ? "Sending..." : "Send Code"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
